if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.minScale = 1;
        this.maxScale = 4;
        this.doubleTapScale = 2.5;
        this.zoomStep = 1.5;
        this.swipeThreshold = 60;

        this.dialog = this.querySelector('[role="dialog"]');
        this.stage = this.querySelector('[role="document"]');
        this.counter = this.querySelector('[data-modal-counter]');
        this.prevButton = this.querySelector('[data-modal-prev]');
        this.nextButton = this.querySelector('[data-modal-next]');
        this.zoomInButton = this.querySelector('[data-modal-zoom-in]');
        this.zoomOutButton = this.querySelector('[data-modal-zoom-out]');

        this.pointers = new Map();
        this.gesture = null;
        this.lastTap = null;
        this.scale = 1;
        this.x = 0;
        this.y = 0;

        // ModalDialog closes media modals on every mouse pointerup, which would make
        // dragging and the controls unusable. Closing is handled in onPointerUp instead.
        this.dialog.addEventListener('pointerup', (event) => event.stopPropagation());

        this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.stage.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.stage.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.stage.addEventListener('pointercancel', this.onPointerUp.bind(this));
        this.stage.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
        this.stage.addEventListener('dragstart', (event) => event.preventDefault());
        // Safari's own pinch gesture would zoom the whole page behind the modal
        this.addEventListener('gesturestart', (event) => event.preventDefault());

        this.addEventListener('keydown', this.onKeyDown.bind(this));
        this.prevButton?.addEventListener('click', () => this.navigate(-1));
        this.nextButton?.addEventListener('click', () => this.navigate(1));
        this.zoomInButton?.addEventListener('click', () => this.zoomTo(this.scale * this.zoomStep));
        this.zoomOutButton?.addEventListener('click', () => this.zoomTo(this.scale / this.zoomStep));

        this.onResize = () => {
          this.clampPosition();
          this.render(false);
        };
      }

      hide() {
        window.removeEventListener('resize', this.onResize);
        this.pointers.clear();
        this.gesture = null;
        this.lastTap = null;
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        this.openedBy = opener;
        const activeMedia = this.showActiveMedia();
        super.show(opener);
        this.loadDeferredMedia(activeMedia);
        window.addEventListener('resize', this.onResize);
      }

      // Variant images hidden by the "hide other variants' media" setting are not part of the gallery
      get slides() {
        return Array.from(this.stage.querySelectorAll(':scope > [data-media-id]')).filter(
          (slide) =>
            slide.classList.contains('active') ||
            slide.matches(':first-child') ||
            !slide.classList.contains('product__media-item--variant')
        );
      }

      get activeMedia() {
        return this.stage.querySelector(':scope > .active');
      }

      get activeImage() {
        const activeMedia = this.activeMedia;
        return activeMedia && activeMedia.nodeName === 'IMG' ? activeMedia : null;
      }

      showActiveMedia() {
        const mediaId = this.openedBy.getAttribute('data-media-id');
        const activeMedia =
          this.stage.querySelector(`:scope > [data-media-id="${mediaId}"]`) ||
          this.stage.querySelector(':scope > [data-media-id]');
        this.setActiveMedia(activeMedia);
        return activeMedia;
      }

      setActiveMedia(activeMedia) {
        if (!activeMedia) return;
        this.resetZoom();
        this.stage.querySelectorAll(':scope > .active').forEach((element) => {
          if (element !== activeMedia) element.classList.remove('active');
        });
        activeMedia.classList.add('active');
        this.updateControls();
      }

      loadDeferredMedia(activeMedia) {
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      navigate(direction) {
        const slides = this.slides;
        if (slides.length < 2) return;
        const currentIndex = Math.max(0, slides.indexOf(this.activeMedia));
        const nextMedia = slides[(currentIndex + direction + slides.length) % slides.length];
        window.pauseAllMedia();
        this.setActiveMedia(nextMedia);
        this.loadDeferredMedia(nextMedia);
      }

      updateControls() {
        const slides = this.slides;
        const hasMultiple = slides.length > 1;
        const isImage = Boolean(this.activeImage);

        [this.prevButton, this.nextButton].forEach((button) => {
          if (!button) return;
          button.classList.toggle('hidden', !hasMultiple);
          button.disabled = !hasMultiple;
        });
        [this.zoomInButton, this.zoomOutButton].forEach((button) => {
          if (!button) return;
          button.classList.toggle('hidden', !isImage);
          button.disabled = !isImage;
        });
        if (this.counter) {
          this.counter.textContent = hasMultiple ? `${slides.indexOf(this.activeMedia) + 1} / ${slides.length}` : '';
        }
        this.zoomInButton?.parentElement.classList.toggle('hidden', !isImage && !hasMultiple);
        this.updateZoomButtons();
      }

      updateZoomButtons() {
        this.zoomInButton?.setAttribute('aria-disabled', this.scale >= this.maxScale);
        this.zoomOutButton?.setAttribute('aria-disabled', this.scale <= this.minScale);
      }

      // Size of the picture as painted inside its (object-fit: contain) box at scale 1
      getImageSize(image) {
        const boxWidth = image.clientWidth;
        const boxHeight = image.clientHeight;
        const ratio =
          image.naturalWidth && image.naturalHeight
            ? image.naturalWidth / image.naturalHeight
            : Number(image.getAttribute('width')) / Number(image.getAttribute('height')) || 1;
        const width = Math.min(boxWidth, boxHeight * ratio);
        return { width, height: width / ratio };
      }

      getStageCenter() {
        const rect = this.stage.getBoundingClientRect();
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      }

      isPointOnImage(clientX, clientY) {
        const image = this.activeImage;
        if (!image) return false;
        const { width, height } = this.getImageSize(image);
        const center = this.getStageCenter();
        return (
          Math.abs(clientX - center.x - this.x) <= (width * this.scale) / 2 &&
          Math.abs(clientY - center.y - this.y) <= (height * this.scale) / 2
        );
      }

      // Keeps the zoomed picture from being dragged past the edges of the screen
      clampPosition() {
        const image = this.activeImage;
        if (!image) return;
        const { width, height } = this.getImageSize(image);
        const maxX = Math.max(0, (width * this.scale - this.stage.clientWidth) / 2);
        const maxY = Math.max(0, (height * this.scale - this.stage.clientHeight) / 2);
        this.x = Math.min(maxX, Math.max(-maxX, this.x));
        this.y = Math.min(maxY, Math.max(-maxY, this.y));
      }

      // Zooms around a point on screen (defaults to the centre of the stage)
      zoomTo(scale, clientX, clientY, animate = true) {
        if (!this.activeImage) return;
        const center = this.getStageCenter();
        const pointX = (clientX ?? center.x) - center.x;
        const pointY = (clientY ?? center.y) - center.y;
        const nextScale = Math.min(this.maxScale, Math.max(this.minScale, scale));
        const factor = nextScale / this.scale;

        this.x = pointX - (pointX - this.x) * factor;
        this.y = pointY - (pointY - this.y) * factor;
        this.scale = nextScale;
        this.clampPosition();
        this.render(animate);
      }

      resetZoom() {
        this.scale = 1;
        this.x = 0;
        this.y = 0;
        this.stage.querySelectorAll(':scope > img').forEach((image) => {
          image.classList.remove('is-animating', 'is-zoomed');
          image.style.transform = '';
        });
        this.updateZoomButtons();
      }

      render(animate) {
        const image = this.activeImage;
        if (!image) return;
        image.classList.toggle('is-animating', animate);
        image.classList.toggle('is-zoomed', this.scale > 1);
        image.style.transform = `translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
        this.updateZoomButtons();
        this.upgradeResolution(image);
      }

      // Ask the browser for a larger srcset candidate once the picture is magnified
      upgradeResolution(image) {
        if (this.scale <= 1) return;
        const neededWidth = Math.ceil((this.getImageSize(image).width * this.scale) / 500) * 500;
        if (neededWidth <= (Number(image.dataset.zoomWidth) || 0)) return;
        image.dataset.zoomWidth = neededWidth;
        image.sizes = `${neededWidth}px`;
      }

      getPointerMetrics() {
        const points = Array.from(this.pointers.values());
        const x = points.reduce((sum, point) => sum + point.x, 0) / points.length;
        const y = points.reduce((sum, point) => sum + point.y, 0) / points.length;
        const distance = points.length > 1 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0;
        return { x, y, distance };
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        // videos and 3D models keep their own controls
        if (event.target.closest('deferred-media, product-model, button')) return;

        if (this.pointers.size === 0) {
          this.gesture = { startX: event.clientX, startY: event.clientY, moved: false };
        } else if (this.gesture) {
          // a second finger makes it a pinch, never a tap or a swipe
          this.gesture.moved = true;
          if (this.scale === 1) this.x = 0;
        }
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        this.activeImage?.classList.remove('is-animating');

        try {
          this.stage.setPointerCapture(event.pointerId);
        } catch (error) {}
      }

      onPointerMove(event) {
        if (!this.pointers.has(event.pointerId) || !this.gesture) return;

        const before = this.getPointerMetrics();
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const after = this.getPointerMetrics();

        if (Math.hypot(event.clientX - this.gesture.startX, event.clientY - this.gesture.startY) > 10) {
          this.gesture.moved = true;
        }
        if (!this.activeImage || !this.gesture.moved) return;

        if (this.pointers.size > 1) {
          // pinch: follow the midpoint of the fingers and scale around it
          this.x += after.x - before.x;
          this.y += after.y - before.y;
          const ratio = before.distance > 0 ? after.distance / before.distance : 1;
          this.zoomTo(this.scale * ratio, after.x, after.y, false);
        } else if (this.scale > 1) {
          this.x += after.x - before.x;
          this.y += after.y - before.y;
          this.clampPosition();
          this.render(false);
        } else if (this.slides.length > 1) {
          // not zoomed: the picture follows the finger sideways, release decides if it is a swipe
          this.x = event.clientX - this.gesture.startX;
          this.render(false);
        }
      }

      onPointerUp(event) {
        if (!this.pointers.delete(event.pointerId)) return;
        try {
          this.stage.releasePointerCapture(event.pointerId);
        } catch (error) {}
        if (this.pointers.size > 0) return;

        const gesture = this.gesture;
        this.gesture = null;
        if (!gesture) return;

        if (this.scale === 1 && Math.abs(this.x) > 0) {
          const swipeDistance = this.x;
          this.x = 0;
          if (event.type === 'pointerup' && Math.abs(swipeDistance) > this.swipeThreshold) {
            this.navigate(swipeDistance < 0 ? 1 : -1);
            return;
          }
        }
        this.clampPosition();
        this.render(true);

        if (gesture.moved || event.type !== 'pointerup') return;

        // tap outside the picture closes the modal, double tap on it toggles zoom
        if (!this.isPointOnImage(event.clientX, event.clientY)) {
          this.hide();
          return;
        }

        const isDoubleTap =
          this.lastTap &&
          event.timeStamp - this.lastTap.time < 300 &&
          Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < 40;

        if (isDoubleTap) {
          this.lastTap = null;
          this.zoomTo(this.scale > 1 ? this.minScale : this.doubleTapScale, event.clientX, event.clientY);
        } else {
          this.lastTap = { time: event.timeStamp, x: event.clientX, y: event.clientY };
        }
      }

      onWheel(event) {
        if (!this.activeImage) return;
        event.preventDefault();
        // ctrlKey is set for trackpad pinch gestures, which report much smaller deltas
        const intensity = event.ctrlKey ? 0.01 : 0.002;
        const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
        this.zoomTo(this.scale * Math.exp(-delta * intensity), event.clientX, event.clientY, false);
      }

      onKeyDown(event) {
        if (event.target.closest('deferred-media, product-model')) return;
        switch (event.key) {
          case 'ArrowLeft':
            this.navigate(-1);
            break;
          case 'ArrowRight':
            this.navigate(1);
            break;
          case '+':
          case '=':
            this.zoomTo(this.scale * this.zoomStep);
            break;
          case '-':
            this.zoomTo(this.scale / this.zoomStep);
            break;
          case '0':
            this.zoomTo(this.minScale);
            break;
          default:
            return;
        }
        event.preventDefault();
      }
    }
  );
}
